import { Button, Col, message, Row, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getInfluencers } from "services/SauceService";
import { useDispatch, useSelector } from "react-redux";
import { SubscriptionType } from "enums/SubscriptionType";
import { BusinessState, getFavoritedCreators } from "redux-context/business";
import CreatorCard from "./CreatorCard";
import Loader from "components/loader/Loader";
import AppliedFIlterChips from "./AppliedFIlterChips";
import FilterAI, { buildQuery } from "./FilterAI";
import DropdownFilterWrapper from "components/dropdown-filter-wrapper/DropdownFilterWrapper";
import {
  faArrowDownShortWide,
  faHashtag,
  faLocationDot,
  faTags,
  faUsersViewfinder,
  faVenusMars,
} from "@fortawesome/free-solid-svg-icons";
import AudienceFilter from "components/audience-filter/AudienceFilter";
import CategoriesTreeFilter from "components/categories-tree-filter/CategoriesTreeFilter";
import GenderFilter from "components/gender-filter/GenderFilter";
import InfluencersSort from "components/influencers-sort/InfluencersSort";
import LocationFilter from "components/location-filter/LocationFilter";
import MinimumThresholds from "components/minimum-thresholds/MinimumThresholds";
import PlatformFilter from "components/platform-filter/PlatformFilter";

enum CreatorTab {
  All = "ALL",
  Applied = "APPLIED",
  Invited = "INVITED",
  Saved = "SAVED",
}

const getFiltersFromURL = (search: string) => {
  const params = new URLSearchParams(search);
  const filter: any = {
    skip: 0,
    limit: 24,
  };
  if (params.has("minAmountFollowers")) {
    filter.minAmountFollowers = Number(params.get("minAmountFollowers"));
  }
  if (params.has("minAmountEngagement")) {
    filter.minAmountEngagement = Number(params.get("minAmountEngagement"));
  }
  if (params.has("categories")) {
    filter.categories = params.get("categories")?.split(";");
  }
  if (params.has("locations")) {
    filter.locations = params.get("locations")?.split(";");
  }
  if (params.has("gender")) {
    filter.gender = params.get("gender")?.split(";");
  }
  if (params.has("socialMediaName")) {
    filter.socialMediaName = params.get("socialMediaName");
  }
  if (params.has("audiences")) {
    filter.audiences = params.get("audiences")?.split(";");
  }
  if (params.has("minAmountEngagement") && !filter.minAmountEngagement) {
    filter.minAmountEngagement = Number(params.get("minAmountEngagement"));
  }
  if (params.has("platform")) {
    filter.platform = params.get("platform");
  }
  if (params.has("sortField")) {
    filter.sortField = params.get("sortField");
  }
  if (params.has("sortDirection")) {
    filter.sortDirection = params.get("sortDirection");
  }
  return filter;
};

const Influencers: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const [filter, setFilter] = useState(getFiltersFromURL(location.search));
  const [selectedTab, setSelectedTab] = useState<CreatorTab>(CreatorTab.All);
  const [localLoading, setLocalLoadingLoading] = useState<boolean>(false);
  const [fieldValues, setFieldValues] = useState<any>({
    ...getFiltersFromURL(location.search),
  });
  const [influencers, setInfluencers] = useState<any>({ data: [], total: 0 });
  const [totalInfluencers, setTotalInfluencers] = useState<number>(0);
  const { subscription, favoritedCreators } = useSelector(
    (state: BusinessState) => state.business
  );
  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;

  const tabOptions = [{ value: "Pending", label: `Applications` }];
  if (!freemium)
    tabOptions.push({ value: "All influencers", label: "All Influencers" });
  const onClickFoodie = (foodieId: string) => {
    navigate(`/creators/${foodieId}`);
  };

  const onClickFilter = () => {
    const queryString = buildQuery(fieldValues).join("&");

    navigate(`/creators?${queryString}`);
  };

  const getAllInfluencers = useCallback(async () => {
    try {
      setLocalLoadingLoading(true);
      const response: any = await getInfluencers(filter);
      setInfluencers((prev: any) => {
        if (filter.skip === 0) {
          return { ...prev, ...response, data: response.data };
        } else {
          return {
            ...prev,
            ...response,
            data: [...prev.data, ...response.data],
          };
        }
      });
      setTotalInfluencers(response.total);
      setLocalLoadingLoading(false);
    } catch (error) {
      setInfluencers({ data: [], total: 0 });
      setLocalLoadingLoading(false);
      message.error("Error fetching creators");
    }
  }, [filter]);

  useEffect(() => {
    setFieldValues(() => ({ ...getFiltersFromURL(location.search) }));
    setFilter(() => ({ ...getFiltersFromURL(location.search) }));
  }, [location.search]);

  useEffect(() => {
    getAllInfluencers();
    dispatch(getFavoritedCreators());
  }, [filter, getAllInfluencers, dispatch]);

  useEffect(() => {
    const handleScroll = () => {
      const nearBottom =
        Math.abs(
          window.innerHeight +
            document.documentElement.scrollTop -
            document.documentElement.offsetHeight
        ) < 10;

      if (nearBottom && !localLoading && selectedTab === CreatorTab.All) {
        setFilter((prevFilter: any) => {
          const nextSkip = prevFilter.skip + prevFilter.limit;
          if (nextSkip < totalInfluencers) {
            return {
              ...prevFilter,
              skip: nextSkip,
            };
          }
          return prevFilter;
        });
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [localLoading, selectedTab, totalInfluencers]);

  return (
    <div className="influencers">
      <Loader visible={localLoading} />

      <Row justify="space-between">
        <FilterAI />
      </Row>

      <div className="creator-tabs">
        <button
          className={`creator-tab${
            selectedTab === CreatorTab.All ? " creator-tab--active" : ""
          }`}
          onClick={() => setSelectedTab(CreatorTab.All)}
        >
          All creators
        </button>
        <button
          className={`creator-tab${
            selectedTab === CreatorTab.Applied ? " creator-tab--active" : ""
          }`}
          onClick={() => setSelectedTab(CreatorTab.Applied)}
          style={{ display: "none" }}
        >
          Applied <span className="creator-tab__badge">24</span>
        </button>
        <button
          className={`creator-tab${
            selectedTab === CreatorTab.Invited ? " creator-tab--active" : ""
          }`}
          onClick={() => setSelectedTab(CreatorTab.Invited)}
          style={{ display: "none" }}
        >
          Invited
        </button>
        <button
          className={`creator-tab${
            selectedTab === CreatorTab.Saved ? " creator-tab--active" : ""
          }`}
          onClick={() => setSelectedTab(CreatorTab.Saved)}
        >
          Saved{" "}
          <span className="creator-tab__badge creator-tab__badge--gray">
            {favoritedCreators?.length}
          </span>
        </button>
      </div>

      {selectedTab === CreatorTab.All && (
        <>
          <Row style={{ gap: 10, marginBottom: 20 }}>
            <DropdownFilterWrapper
              btnText="Platform"
              btnIcon={faHashtag}
              title="Platform Filter"
              subtitle="Select social media platforms"
              onFilter={onClickFilter}
              onClear={() =>
                setFieldValues((prev: any) => ({ ...prev, platform: "" }))
              }
            >
              <PlatformFilter
                onChangeValues={(val: any) =>
                  setFieldValues((prev: any) => ({ ...prev, platform: val }))
                }
                values={fieldValues?.platform}
              />
            </DropdownFilterWrapper>
            <DropdownFilterWrapper
              btnText="Min"
              btnIcon={faHashtag}
              title="Minimum Thresholds"
              subtitle="Set minimum follower count and engagement rate"
              onFilter={onClickFilter}
              onClear={() =>
                setFieldValues((prev: any) => ({
                  ...prev,
                  minAmountFollowers: null,
                  minAmountEngagement: null,
                }))
              }
            >
              <MinimumThresholds
                onChangeValues={(val: any) =>
                  setFieldValues((prev: any) => ({ ...prev, ...val }))
                }
                values={{
                  minAmountFollowers: fieldValues?.minAmountFollowers,
                  minAmountEngagement: fieldValues?.minAmountEngagement,
                }}
              />
            </DropdownFilterWrapper>
            <DropdownFilterWrapper
              btnText="Categories"
              btnIcon={faTags}
              title="Categories Filter"
              subtitle="Search and select creator categories"
              onFilter={onClickFilter}
              onClear={() =>
                setFieldValues((prev: any) => ({ ...prev, categories: "" }))
              }
            >
              <CategoriesTreeFilter
                onChangeValues={(val: any) =>
                  setFieldValues((prev: any) => ({
                    ...prev,
                    categories: val,
                  }))
                }
                values={fieldValues?.categories}
                numColumns={4}
              />
            </DropdownFilterWrapper>
            <DropdownFilterWrapper
              btnText="Audiences"
              btnIcon={faUsersViewfinder}
              title="Audience Demographics"
              subtitle="Select target audience age groups and gender"
              onFilter={onClickFilter}
              onClear={() =>
                setFieldValues((prev: any) => ({ ...prev, audiences: "" }))
              }
            >
              <AudienceFilter
                onChangeValues={(val: any) =>
                  setFieldValues((prev: any) => ({ ...prev, audiences: val }))
                }
                values={fieldValues?.audiences}
              />
            </DropdownFilterWrapper>
            <DropdownFilterWrapper
              btnText="Location"
              btnIcon={faLocationDot}
              title="Location Filter"
              subtitle="Search by country, state/province/county, and city"
              onFilter={onClickFilter}
              onClear={() =>
                setFieldValues((prev: any) => ({ ...prev, locations: [] }))
              }
            >
              <LocationFilter
                onChangeValues={(val: any) =>
                  setFieldValues((prev: any) => ({ ...prev, locations: val }))
                }
                values={fieldValues?.locations}
              />
            </DropdownFilterWrapper>

            <DropdownFilterWrapper
              btnText="Gender"
              btnIcon={faVenusMars}
              title="Gender Filter"
              subtitle="Select preferred creator gender."
              onFilter={onClickFilter}
              onClear={() =>
                setFieldValues((prev: any) => ({ ...prev, gender: "" }))
              }
            >
              <GenderFilter
                onChangeValues={(val: any) =>
                  setFieldValues((prev: any) => ({ ...prev, gender: val }))
                }
                values={fieldValues?.gender}
              />
            </DropdownFilterWrapper>

            <DropdownFilterWrapper
              btnText="Sort"
              btnIcon={faArrowDownShortWide}
              title="Sort By"
              subtitle="Choose how to order creators"
              onFilter={onClickFilter}
              onClear={() =>
                setFieldValues((prev: any) => ({
                  ...prev,
                  sortField: undefined,
                  sortDirection: undefined,
                }))
              }
            >
              <InfluencersSort
                onChangeValues={(val: any) =>
                  setFieldValues((prev: any) => ({ ...prev, ...val }))
                }
                values={{
                  sortField: fieldValues?.sortField,
                  sortDirection: fieldValues?.sortDirection,
                }}
              />
            </DropdownFilterWrapper>
          </Row>

          <Row>
            <Col xs={24} className="container-amount-creators">
              <Typography.Text>
                {totalInfluencers?.toLocaleString()} creators found
              </Typography.Text>
              <Button
                className={`clear-all-button ${
                  location.search.length > 0 ? "visible" : "hidden"
                }`}
                type="link"
                onClick={() => navigate("/creators")}
              >
                Clear all filters
              </Button>
            </Col>
            <Col xs={24}>
              <AppliedFIlterChips />
            </Col>
          </Row>
          <Row style={{ gap: 16, marginTop: location.search ? 10 : 16 }}>
            {influencers.data?.map((influencer: any, idx: number) => (
              <CreatorCard
                creator={influencer}
                onInfluencerClick={() => onClickFoodie(influencer.foodieId)}
                key={`INFLUENCER_${influencer.foodieId}_${influencer.collabId}_${idx}`}
                index={idx}
              />
            ))}
          </Row>
        </>
      )}

      {selectedTab === CreatorTab.Saved && (
        <>
          <Row style={{ gap: 16, marginTop: 24 }}>
            {favoritedCreators?.map((influencer: any, idx: number) => (
              <CreatorCard
                creator={influencer}
                onInfluencerClick={() => onClickFoodie(influencer.foodieId)}
                key={`INFLUENCER_${influencer.foodieId}_${influencer.collabId}_${idx}`}
                index={idx}
              />
            ))}
          </Row>
        </>
      )}
    </div>
  );
};

export default Influencers;
